import { useEffect, useState } from 'react';
import { Copy, Pencil, Save, X, QrCode, Sparkles } from 'lucide-react';
import InviteQrShare from './InviteQrShare';

/**
 * Celda de código de invitación: visible, copiable, editable, generable y QR para compartir.
 */
export default function InviteCodeCell({
  value,
  onSave,
  onGenerate = null,
  readOnly = false,
  placeholder = 'Sin código',
  shareLabel = 'Invitación',
  generateLabel = 'Generar código',
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showQr, setShowQr] = useState(false);

  const display = value?.trim() ? value.trim() : null;

  useEffect(() => {
    if (!editing) setDraft(display || '');
  }, [display, editing]);

  const copyCode = async () => {
    if (!display) return;
    try {
      await navigator.clipboard.writeText(display);
    } catch {
      /* noop */
    }
  };

  const startEdit = () => {
    setDraft(display || '');
    setLocalError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(display || '');
    setLocalError('');
  };

  const save = async () => {
    setSaving(true);
    setLocalError('');
    try {
      const trimmed = draft.trim();
      if (!trimmed && onGenerate) {
        await handleGenerate(true);
        setEditing(false);
        return;
      }
      if (!trimmed) {
        setLocalError('Escribe un código o usa Generar');
        return;
      }
      await onSave(trimmed);
      setEditing(false);
    } catch (err) {
      const data = err?.response?.data;
      setLocalError(typeof data === 'string' ? data : data?.error || 'No se pudo guardar el código');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async (andSave = false) => {
    if (!onGenerate) return;
    setGenerating(true);
    setLocalError('');
    try {
      const code = await onGenerate();
      if (code) {
        setDraft(code);
        if (andSave && onSave) {
          await onSave(code);
        }
      }
    } catch (err) {
      const data = err?.response?.data;
      setLocalError(typeof data === 'string' ? data : data?.error || 'No se pudo generar el código');
    } finally {
      setGenerating(false);
    }
  };

  if (readOnly) {
    return (
      <>
        <div className="flex items-center gap-1 flex-wrap">
          <code className="text-xs font-mono bg-stone-100 text-stone-800 px-2 py-1 rounded border border-stone-200">
            {display || placeholder}
          </code>
          {display && (
            <>
              <button type="button" className="p-1 text-stone-500 hover:text-stone-800" onClick={copyCode} title="Copiar código">
                <Copy size={14} />
              </button>
              <button type="button" className="p-1 text-stone-500 hover:text-lime-700" onClick={() => setShowQr(true)} title="Ver QR">
                <QrCode size={14} />
              </button>
            </>
          )}
        </div>
        {showQr && display ? (
          <InviteQrShare code={display} label={shareLabel} onClose={() => setShowQr(false)} />
        ) : null}
      </>
    );
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1 min-w-[200px]">
        <div className="flex items-center gap-1 flex-wrap">
          <input
            type="text"
            className="input text-xs font-mono uppercase py-1 flex-1 min-w-[140px]"
            value={draft}
            onChange={(e) => setDraft(e.target.value.toUpperCase())}
            placeholder="GYM-EJEMPLO-001"
            autoFocus
          />
          {onGenerate && (
            <button
              type="button"
              className="btn-secondary text-xs px-2 py-1 inline-flex items-center gap-1 shrink-0"
              onClick={() => handleGenerate(false)}
              disabled={generating || saving}
              title={generateLabel}
            >
              <Sparkles size={12} />
              {generating ? '…' : 'Generar'}
            </button>
          )}
          <button
            type="button"
            className="p-1.5 rounded-lg bg-lime-500 text-black hover:bg-lime-400 disabled:opacity-50"
            onClick={save}
            disabled={saving || generating}
            title="Guardar"
          >
            <Save size={14} />
          </button>
          <button type="button" className="p-1.5 rounded-lg border border-stone-300 hover:bg-stone-100" onClick={cancelEdit} title="Cancelar">
            <X size={14} />
          </button>
        </div>
        {localError && <span className="text-xs text-red-600">{localError}</span>}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-1 flex-wrap">
        <code className="text-xs font-mono bg-lime-50 border border-lime-200 text-lime-900 px-2 py-1 rounded">
          {display || <span className="text-stone-400">{placeholder}</span>}
        </code>
        {display && (
          <>
            <button type="button" className="p-1 text-stone-500 hover:text-stone-800" onClick={copyCode} title="Copiar código">
              <Copy size={14} />
            </button>
            <button type="button" className="p-1 text-stone-500 hover:text-lime-700" onClick={() => setShowQr(true)} title="QR para compartir">
              <QrCode size={14} />
            </button>
          </>
        )}
        {!display && onGenerate && (
          <button
            type="button"
            className="btn-primary text-xs px-2.5 py-1 inline-flex items-center gap-1"
            onClick={() => handleGenerate(true)}
            disabled={generating}
          >
            <Sparkles size={12} />
            {generating ? 'Generando…' : generateLabel}
          </button>
        )}
        <button type="button" className="p-1 text-stone-500 hover:text-lime-700" onClick={startEdit} title="Editar código">
          <Pencil size={14} />
        </button>
      </div>
      {localError && !editing && <span className="text-xs text-red-600 block mt-0.5">{localError}</span>}
      {showQr && display ? (
        <InviteQrShare code={display} label={shareLabel} onClose={() => setShowQr(false)} />
      ) : null}
    </>
  );
}
